import React, { useCallback, useState } from 'react';
import { Backdrop, Box, Button, Fade, IconButton, Modal, Paper, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { API, graphqlOperation } from 'aws-amplify';

import { hideSettings } from '../../redux/reducers/sidebar';
import { resetNavigationState, setCustomModalAction, setModalOpen } from '../../redux/reducers/controllNavigation';
import useControlledNavigation from '../../hooks/useControlledNavigation';
import useSaveUserPreference from '../../hooks/useSaveUserPreference';

import { deleteFinancialYear, createCompanyPreference } from '../../graphql';
import { showErrorSnackbar } from '../snackBar/snakbar';

import { setRemoveCompanyRelatedFY } from '../../redux/reducers/CompanySlice';
import { fetchUserPreference, setRemoveFY } from '../../redux/reducers/UserPreference';
import { resetState } from '../../redux/reducers/FYSetupStep';
import {
  companyPreferenceApi,
  useCreateCompanyPreferenceMutation,
  useListCompanyPreferenceQuery,
} from '../../redux/apis/companyPreference.api';
import { CompanyPreference } from '../../types';
import { isBlock } from 'typescript';
import { useLocation } from 'react-router-dom';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  boxShadow: 24,
  outline: 'none',
  width: '90%',
  '@media (min-width:600px)': {
    minWidth: '400px',
    width: 'auto',
  },
};

const ConfirmExitNew = () => {
  const dispatch = useDispatch();
  const navigate = useControlledNavigation();
  const location = useLocation();
  const { saveUserPreference } = useSaveUserPreference();

  const [isLoading, setIsLoading] = useState(false);

  const { isModalOpen, blockedPath, lastAllowedPath, customModalTrigger, modalName, onLeaveFunction, companyAndFinancialYearSwitch } =
    useSelector((state) => state.controllNavigationSlice, shallowEqual);

  const { data: companyPreference = [], isSuccess } = useListCompanyPreferenceQuery({});

  const { companyDetails } = useSelector((state) => state.companyReducer || {});

  const path = typeof lastAllowedPath === 'string' ? lastAllowedPath : '';
  const isFYCreatePage = [path, location.pathname].some((route) => route?.includes('/create-new-financial-year'));
  const isSettingsOpen = useSelector((state) => state.sidebarReducer?.settings, shallowEqual);

  const handleModalClose = useCallback(() => {
    dispatch(setModalOpen(false));
    dispatch(setCustomModalAction({ key: 'onLeaveFunction', value: null }));
    dispatch(setCustomModalAction({ key: 'modalName', value: '' }));
  }, [dispatch]);

  const deleteFinancialYearRecord = useCallback(async (financialyearid) => {
    try {
      await API.graphql(graphqlOperation(deleteFinancialYear, { financialyearid }));
    } catch (error) {
      console.error('Failed to delete financial year:', error);
      showErrorSnackbar('Failed to delete financial year');
      throw error;
    } finally {
      localStorage.removeItem('fyDeletionInProgress');
      localStorage.removeItem('fyToDelete');
      localStorage.removeItem('companyId');
    }
  }, []);

  const [createCompanyPreference, { isLoading: creating }] = useCreateCompanyPreferenceMutation();

  const createCompanyPreferenceRecord = useCallback(async () => {
    try {
      const userPreferenceConfig = {
        additionalvalue: '',
        attributetypecd: 'NewFY Creation',
        attributevalue: 0,
      };

      await createCompanyPreference({
        createCompanyPreferenceInput: userPreferenceConfig,
      });
    } catch (error) {
      console.error('Failed to create company preference:', error);
      showErrorSnackbar('Failed to create company preference');
      throw error;
    }
  }, []);

  const handleIncompleteFinancialYear = useCallback(
    async (data) => {
      localStorage.setItem('fyDeletionInProgress', 'true');
      localStorage.setItem('fyToDelete', data?.financialyearid);
      localStorage.setItem('companyId', data?.companyid);

      try {
        await deleteFinancialYearRecord(data.financialyearid);
        await createCompanyPreferenceRecord();
      } catch (error) {
        throw error;
      }
    },
    [deleteFinancialYearRecord, createCompanyPreferenceRecord]
  );

  const dispatchUpdates = useCallback(
    (data) => {
      dispatch(setRemoveFY(data.financialyearid));
      dispatch(
        setRemoveCompanyRelatedFY({
          companyid: data.companyid,
          fyid: data.financialyearid,
        })
      );
      dispatch(fetchUserPreference());
    },
    [dispatch]
  );

  const isModalCase = modalName === 'partyModal' || modalName === 'stockModal' || modalName === 'itemModal';

  const handleLeave = useCallback(async () => {
    setIsLoading(true);
    try {
      if (isSettingsOpen) {
        dispatch(hideSettings());
      }

      if (customModalTrigger) {
        dispatch(setCustomModalAction({ key: 'isCustomModalOpen', value: true }));
      }

      let destinationPath = blockedPath;

      if (isFYCreatePage) {
        const newFYPref = companyPreference.find((pref) => pref.attributetypecd === 'NewFY Creation' && Number(pref.attributevalue) > 1);
        const [fromStr, toStr, option, startStr] = newFYPref.additionalvalue.split('-');
        const rollBackData = {
          financialyearid: Number(toStr),
          companyid: Number(companyDetails?.companyid),
        };
        await handleIncompleteFinancialYear(rollBackData);
        dispatchUpdates(rollBackData);
        destinationPath = '/';
      }

      if (modalName === 'logout' || modalName === 'guidedNav') {
        await onLeaveFunction();
        dispatch(resetNavigationState());
        return;
      }

      if (modalName === 'stockModal' || modalName === 'partyModal' || modalName === 'itemModal') {
        await onLeaveFunction();
      }

      if (companyAndFinancialYearSwitch?.isPending) {
        const { targetCompanyId, targetFinancialYearId, message } = companyAndFinancialYearSwitch;
        await saveUserPreference(targetFinancialYearId, targetCompanyId, 'fy', message);
        destinationPath = '/';
      }

      if (modalName !== 'partyModal' && modalName !== 'itemModal' && modalName !== 'stockModal') {
        navigate(destinationPath, { force: true });
        dispatch(resetNavigationState());
      } else {
        dispatch(setCustomModalAction({ key: 'modalName', value: '' }));
        dispatch(setCustomModalAction({ key: 'isModalOpen', value: false }));
         dispatch(setCustomModalAction({ key: 'isCheckModalBlocked', value: false }));
        dispatch(
          setCustomModalAction({
            key: 'onLeaveFunction',
            value: null,
          })
        );
      }
      handleModalClose();
      dispatch(resetState());
    } catch (error) {
      showErrorSnackbar('An error occurred while leaving the page');
    } finally {
      setIsLoading(false);
    }
  }, [
    dispatch,
    isSettingsOpen,
    customModalTrigger,
    isFYCreatePage,
    handleIncompleteFinancialYear,
    dispatchUpdates,
    companyPreference,
    companyDetails?.companyid,
    modalName,
    onLeaveFunction,
    companyAndFinancialYearSwitch,
    blockedPath,
    saveUserPreference,
    handleModalClose,
    navigate,
  ]);

  return (
    <Modal
      id={isModalOpen ? 'leavePage' : undefined}
      disableRestoreFocus
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={isModalOpen}
      onClose={handleModalClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
    >
      <Fade in={isModalOpen}>
        <Paper sx={modalStyle}>
          <Box
            sx={{
              backgroundColor: 'primary.main',
              padding: '8px 16px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Typography
              sx={{
                color: '#fff',
                flexGrow: 1,
                fontWeight: 600,
              }}
            >
              Changes are not saved
            </Typography>
            <IconButton aria-label="Close" onClick={handleModalClose} size="small">
              <CloseIcon sx={{ color: '#fff' }} />
            </IconButton>
          </Box>

          <Box sx={{ padding: '16px' }}>
            <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
              {modalName === 'partyModal' || modalName === 'stockModal' || modalName === 'itemModal'
                ? 'Are you sure you want to close this window without saving your changes?'
                : 'Are you sure you want to leave this page without saving your changes?'}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '0 16px 16px',
            }}
          >
            <Button
              onClick={handleModalClose}
              variant="outlined"
              sx={{
                flex: 1,
                marginRight: 1,
                fontSize: { xs: 11, sm: 12 },
              }}
            >
              {isModalCase ? 'Stay on Modal' : 'Stay on Page'}
            </Button>
            <LoadingButton
              onClick={handleLeave}
              variant="contained"
              sx={{
                flex: 1,
                marginLeft: 1,
                fontSize: { xs: 11, sm: 12 },
              }}
              loading={isLoading}
            >
              {isModalCase ? 'Close Modal' : 'Leave Page'}
            </LoadingButton>
          </Box>
        </Paper>
      </Fade>
    </Modal>
  );
};

export default ConfirmExitNew;
